import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { VisaType } from '../services/chatService';
import { gtvService } from '../domain/global_talent_visa/services';
import { touristVisaService } from '../domain/tourist_visa/services';
import { ApiError, apiService } from '../services/apiService';

// Enhanced message interface for backend integration
export interface VisaChatMessage {
    id: string;
    content: string;
    sender: 'user' | 'assistant';
    timestamp: string; // ISO string from backend
    response_id?: string;
    status?: string;
}

// Chat session interface
export interface VisaChatSession {
    id: string;
    userId: string;
    visaType: string;
    title: string;
    createdAt: string;
    updatedAt: string;
    lastMessage?: {
        content: string;
        timestamp: string;
        sender: 'user' | 'assistant';
    };
    messageCount: number;
    isActive: boolean;
    metadata?: Record<string, unknown>;
}

// Chat state interface
export interface VisaChatState {
    messages: VisaChatMessage[];
    currentSession: VisaChatSession | null;
    sessions: VisaChatSession[];
    isLoading: boolean;
    isSending: boolean;
    error: string | null;
    hasStarted: boolean;
}

// Hook configuration
interface UseVisaChatProps {
    visaType: VisaType;
    chatId?: string; // Optional specific chat session to load
}

// Hook return interface
interface UseVisaChatReturn extends VisaChatState {
    startChat: (title?: string) => Promise<void>;
    sendMessage: (content: string) => Promise<void>;
    loadChatSession: (chatId: string) => Promise<void>;
    switchToSession: (chatId: string) => Promise<void>;
    createNewSession: (title?: string) => Promise<void>;
    updateSessionTitle: (chatId: string, title: string) => Promise<void>;
    deleteSession: (chatId: string) => Promise<void>;
    clearError: () => void;
    resetChat: () => void;
}

// Get the appropriate service based on visa type
const getVisaService = (visaType: VisaType) => {
    switch (visaType) {
        case VisaType.GLOBAL_TALENT:
            return gtvService;
        case VisaType.TOURIST:
            return touristVisaService;
        default:
            throw new Error(`Unsupported visa type: ${visaType}`);
    }
};

// Get the API base path for the visa type
const getVisaApiPath = (visaType: VisaType): string => {
    switch (visaType) {
        case VisaType.GLOBAL_TALENT:
            return '/api/domain/global_talent_visa/chat';
        case VisaType.TOURIST:
            // TODO: Implement tourist visa chat routes
            throw new Error('Tourist visa chat not yet implemented');
        default:
            throw new Error(`Unsupported visa type: ${visaType}`);
    }
};

// Get visa type display name
const getVisaDisplayName = (visaType: VisaType): string => {
    switch (visaType) {
        case VisaType.GLOBAL_TALENT:
            return 'Global Talent Visa';
        case VisaType.TOURIST:
            return 'Tourist Visa';
        default:
            return 'Visa';
    }
};

export const useVisaChat = ({ visaType, chatId }: UseVisaChatProps): UseVisaChatReturn => {
    const { currentUser } = useAuth();

    // Chat state
    const [messages, setMessages] = useState<VisaChatMessage[]>([]);
    const [currentSession, setCurrentSession] = useState<VisaChatSession | null>(null);
    const [sessions, setSessions] = useState<VisaChatSession[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasStarted, setHasStarted] = useState(false);

    // Get the appropriate service for this visa type
    const visaService = getVisaService(visaType);
    const visaDisplayName = getVisaDisplayName(visaType);
    const apiBasePath = getVisaApiPath(visaType);

    // Load user's chat sessions
    const loadSessions = useCallback(async () => {
        if (!currentUser) return;

        try {
            const response = await apiService.get<{
                status: string;
                chat_sessions: VisaChatSession[];
            }>(`${apiBasePath}/sessions`);

            if (response.status === 'success') {
                setSessions(response.chat_sessions);
            }
        } catch (err) {
            console.error('Error loading chat sessions:', err);
            setError('Failed to load chat sessions');
        }
    }, [currentUser, apiBasePath]);

    // Load a specific chat session with messages
    const loadChatSession = useCallback(async (sessionId: string) => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await apiService.get<{
                status: string;
                chat_session: VisaChatSession;
                messages: VisaChatMessage[];
            }>(`${apiBasePath}/sessions/${sessionId}`);

            if (response.status === 'success') {
                setCurrentSession(response.chat_session);
                setMessages(response.messages);
                setHasStarted(true);
            }
        } catch (err) {
            console.error('Error loading chat session:', err);
            if (err instanceof ApiError) {
                setError(err.message);
            } else {
                setError('Failed to load chat session');
            }
        } finally {
            setIsLoading(false);
        }
    }, [apiBasePath]);

    // Create a new chat session
    const createNewSession = useCallback(async (title?: string) => {
        if (!currentUser) return;

        try {
            setIsLoading(true);
            setError(null);

            const response = await apiService.post<{
                status: string;
                chat_session: VisaChatSession;
            }>(`${apiBasePath}/sessions`, {
                title: title || `${visaDisplayName} Chat`
            });

            if (response.status === 'success') {
                setCurrentSession(response.chat_session);
                setMessages([]);
                setHasStarted(true);

                // Reload sessions to include the new one
                await loadSessions();
            }
        } catch (err) {
            console.error('Error creating chat session:', err);
            if (err instanceof ApiError) {
                setError(err.message);
            } else {
                setError('Failed to create chat session');
            }
        } finally {
            setIsLoading(false);
        }
    }, [currentUser, visaDisplayName, loadSessions, apiBasePath]);

    // Start the chat (create session or load existing)
    const startChat = useCallback(async (title?: string) => {
        if (chatId) {
            // Load specific chat session
            await loadChatSession(chatId);
        } else {
            // Create new session
            await createNewSession(title);
        }
    }, [chatId, loadChatSession, createNewSession]);

    // Switch to a different session
    const switchToSession = useCallback(async (sessionId: string) => {
        await loadChatSession(sessionId);
    }, [loadChatSession]);

    // Send a message to the current session
    const sendMessage = useCallback(async (content: string) => {
        if (!content.trim() || isSending || !currentSession) return;

        try {
            setIsSending(true);
            setError(null);

            // Add user message immediately to UI
            const userMessage: VisaChatMessage = {
                id: `temp_user_${Date.now()}`,
                content: content.trim(),
                sender: 'user',
                timestamp: new Date().toISOString(),
            };

            setMessages(prev => [...prev, userMessage]);

            // Get the last assistant message's response_id for context
            const lastAssistantMessage = messages
                .filter(msg => msg.sender === 'assistant')
                .pop();

            // Send to API with chat_id
            await visaService.sendChatMessage({
                message: content.trim(),
                previous_response_id: lastAssistantMessage?.response_id,
                chat_id: currentSession.id,
            });

            // Remove temporary user message and reload session to get actual messages
            await loadChatSession(currentSession.id);

        } catch (err) {
            // Remove temporary user message on error
            setMessages(prev => prev.filter(msg => msg.id !== `temp_user_${Date.now()}`));

            let errorMessage = 'Failed to send message';
            if (err instanceof ApiError) {
                errorMessage = err.message;
            } else if (err instanceof Error) {
                errorMessage = err.message;
            }

            setError(errorMessage);
            console.error('Error sending message:', err);
        } finally {
            setIsSending(false);
        }
    }, [isSending, currentSession, messages, visaService, loadChatSession]);

    // Update session title
    const updateSessionTitle = useCallback(async (sessionId: string, title: string) => {
        try {
            const response = await apiService.put<{
                status: string;
                chat_session: VisaChatSession;
            }>(`${apiBasePath}/sessions/${sessionId}`, {
                title
            });

            if (response.status === 'success') {
                // Update current session if it's the one being updated
                if (currentSession?.id === sessionId) {
                    setCurrentSession(response.chat_session);
                }

                // Reload sessions
                await loadSessions();
            }
        } catch (err) {
            console.error('Error updating session title:', err);
            if (err instanceof ApiError) {
                setError(err.message);
            } else {
                setError('Failed to update session title');
            }
        }
    }, [currentSession, loadSessions, apiBasePath]);

    // Delete a session
    const deleteSession = useCallback(async (sessionId: string) => {
        try {
            await apiService.delete(`${apiBasePath}/sessions/${sessionId}`);

            // If we're deleting the current session, reset state
            if (currentSession?.id === sessionId) {
                setCurrentSession(null);
                setMessages([]);
                setHasStarted(false);
            }

            // Reload sessions
            await loadSessions();
        } catch (err) {
            console.error('Error deleting session:', err);
            if (err instanceof ApiError) {
                setError(err.message);
            } else {
                setError('Failed to delete session');
            }
        }
    }, [currentSession, loadSessions, apiBasePath]);

    // Clear error
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // Reset chat to initial state
    const resetChat = useCallback(() => {
        setMessages([]);
        setCurrentSession(null);
        setHasStarted(false);
        setError(null);
        setIsLoading(false);
        setIsSending(false);
    }, []);

    // Load sessions on mount
    useEffect(() => {
        if (currentUser) {
            loadSessions();
        }
    }, [currentUser, loadSessions]);

    // Load specific chat session if chatId is provided
    useEffect(() => {
        if (currentUser && chatId) {
            loadChatSession(chatId);
        }
    }, [currentUser, chatId, loadChatSession]);

    return {
        // State
        messages,
        currentSession,
        sessions,
        isLoading,
        isSending,
        error,
        hasStarted,

        // Actions
        startChat,
        sendMessage,
        loadChatSession,
        switchToSession,
        createNewSession,
        updateSessionTitle,
        deleteSession,
        clearError,
        resetChat,
    };
}; 